import React, { useEffect, useRef, useState } from 'react';
import { HandIcon, Volume2Icon } from 'lucide-react';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { alivenessTraits } from '../../data/aliveness';
import { cancelSpeech, speakDutch } from '../../utils/tts';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import type { AvatarState } from '../../types/anna';
const SAMPLE = 'Goedemorgen. Je eerste afspraak is om half tien, en het blijft vandaag droog.';
const DEMO_STATES: {
  id: AvatarState;
  label: string;
}[] = [{
  id: 'rust',
  label: 'Rust'
}, {
  id: 'luistert',
  label: 'Luistert'
}, {
  id: 'denkt',
  label: 'Denkt'
}, {
  id: 'bezorgd',
  label: 'Bezorgd'
}];
export function AlivenessDemo() {
  const {
    soundOn,
    aliveness
  } = useSpecSettings();
  const [state, setState] = useState<AvatarState>('rust');
  const [speaking, setSpeaking] = useState(false);
  const fallbackRef = useRef<number>();
  useEffect(() => () => {
    window.clearTimeout(fallbackRef.current);
    cancelSpeech();
  }, []);
  const speak = () => {
    if (speaking) {
      cancelSpeech();
      window.clearTimeout(fallbackRef.current);
      setSpeaking(false);
      return;
    }
    setSpeaking(true);
    const ok = soundOn && speakDutch(SAMPLE, {
      onEnd: () => setSpeaking(false)
    });
    if (!ok) fallbackRef.current = window.setTimeout(() => setSpeaking(false), 4200);
  };
  const shown: AvatarState = speaking ? 'spreekt' : state;
  return <div className="mt-24 grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-16" data-id="element-708" data-mp="3hy6n8kq5hz9">
      <div className="flex flex-col" data-id="element-709" data-mp="3uo9bzgwrp4we">
        <div className="relative grid aspect-square place-items-center overflow-hidden rounded-3xl border border-white/[0.06] bg-night-900" data-id="element-710" data-mp="3upantnllxvf6">
          <div className="aspect-square h-[88%]" data-id="element-711" data-mp="3pr97hafkga7e">
            <AnnaAvatar state={shown} data-id="element-712" data-mp="1sx4isgf6rw9y" />
          </div>
          <p className="pointer-events-none absolute left-5 top-5 flex items-center gap-2 text-[13px] text-ink-muted" data-id="element-713" data-mp="1suidknfrpsuw">
            <HandIcon className="h-3.5 w-3.5" aria-hidden data-id="element-714" data-mp="3ie9va4w9yta0" /> Beweeg rond Anna of tik op haar kern
          </p>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-715" data-mp="3n505bvfnbkb1">
          <div role="radiogroup" aria-label="Toestand" className="flex rounded-full bg-white/[0.05] p-1" data-id="element-716" data-mp="38fu7aasjo04q">
            {DEMO_STATES.map(s => {
            const active = !speaking && state === s.id;
            return <button key={s.id} type="button" role="radio" aria-checked={active} onClick={() => {
              if (speaking) speak();
              setState(s.id);
            }} className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ${active ? 'bg-white/[0.12] text-ink' : 'text-ink-muted hover:text-ink'}`} data-id="element-717" data-mp="4yu3hm5oebms">
                  {s.label}
                </button>;
          })}
          </div>
          <button type="button" onClick={speak} aria-pressed={speaking} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-150 ${speaking ? 'bg-accent/20 text-ink' : 'bg-white/[0.05] text-ink-soft hover:bg-white/[0.1] hover:text-ink'}`} data-id="element-718" data-mp="2d8tg4yfqaw70">
            <Volume2Icon className="h-3.5 w-3.5" aria-hidden data-id="element-719" data-mp="1ph5zd9c6l1ig" />
            {speaking ? 'Stop' : 'Laat Anna iets zeggen'}
          </button>
        </div>
      </div>

      <div data-id="element-720" data-mp="247ljslwrw5fk">
        <h3 className="font-display text-[28px] font-light leading-tight text-ink lg:text-[32px]" data-id="element-721" data-mp="247dbl7blhoap">Levendigheid</h3>
        <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-ink-soft" data-id="element-722" data-mp="1zdhbnzbrtrcw">
          Bovenop elke toestand leeft een laag kleine, onregelmatige gedragingen. Nooit herhalend, nooit opvallend: je
          merkt het pas als het weg is. Staat nu op{' '}
          <span className="text-ink" data-id="element-723" data-mp="a0kqe22st1a4">{aliveness === 'lively' ? 'levendig' : 'kalm'}</span>; bij ‘minder beweging’ valt
          alles terug naar stilstand.
        </p>
        <dl className="mt-8 divide-y divide-white/[0.06] border-y border-white/[0.06]" data-id="element-724" data-mp="izfy5ilhqjfj">
          {alivenessTraits.map(trait => <div key={trait.id} className="grid gap-1 py-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6" data-id="element-725" data-mp="nrg3xro8s6k1">
              <dt className="text-[15px] font-medium text-ink" data-id="element-726" data-mp="sp97ecy5umgm">{trait.title}</dt>
              <dd data-id="element-727" data-mp="xnsyssviwqgd">
                <p className="text-[14px] leading-relaxed text-ink-soft" data-id="element-728" data-mp="xnarn17borto">{trait.body}</p>
                <p className="mt-1 text-[12px] text-ink-muted" data-id="element-729" data-mp="11rqv6vtgrn5q">{trait.trigger}</p>
              </dd>
            </div>)}
        </dl>
      </div>
    </div>;
}